import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import DropDown from '../../../components/dropDown';
import DataGridComponent from '../../../components/datagrid';
import {
  incorrectCaseReport,
  fstOvertimeReport,
  benchRepairReport,
  rltWorklogReport,
  commAuditLog,
  czSurvey,
  clccSurvey,
  californiaPTLReport,
  forwardToSiteReport,
  gnieUpdateRequestReport,
  retailerInfoChangeReport,
  riCreditLogReport,
  washingtonRetailerTrainingRequestReport,
  consumableSupplyReport,
  nyKeyRequestReport,
  dispatchStatusChangeReport,
  nrcDowntimeReport,
  antillesCreditLogReport,
  miIncidentLogReport,
  njSuppressedReport,
  nyAmLogReport,
  stolenTicketLogReport,
  texasScratchTicketReport,
  itsTexasScratchTicketReport,
  fieldServiceScoreCard,
} from './reportConfigs';
import { reportMenuData } from './reportMenuData';
import { LanguageContext } from '../../../context/languageContext';
import { useCopilotChat } from '../../copilot/copilotChatContext';

const Reports = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const { translations } = useContext(LanguageContext);

  const [selectedCountry, setSelectedCountry] = useState(
    queryParams.get('country') || 'United States',
  );
  const [selectedTeam, setSelectedTeam] = useState(
    queryParams.get('team') || '',
  );
  const [selectedReport, setSelectedReport] = useState(
    queryParams.get('report') || '',
  );
  const { isOpen: isCopilotOpen, panelWidth } = useCopilotChat();
  const [showReport, setShowReport] = useState(false);
  const [isMenuVisible, setIsMenuVisible] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const teams =
    reportMenuData(translations).find(data => data.country === selectedCountry)
      ?.teams || [];
  const reports = teams.find(team => team.name === selectedTeam)?.reports || [];

  const desktopReportsWidth = isCopilotOpen
    ? `min(90vw, calc(100vw - ${panelWidth + 56}px))`
    : '90vw';

  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedCountry) params.set('country', selectedCountry);
    if (selectedTeam) params.set('team', selectedTeam);
    if (selectedReport) params.set('report', selectedReport);
    navigate({ search: params.toString() });
  }, [selectedCountry, selectedTeam, selectedReport, navigate]);

  const handleCountryChange = event => {
    setSelectedCountry(event.target.value);
    setSelectedTeam('');
    setSelectedReport('');
    setShowReport(false);
  };

  const handleTeamChange = event => {
    setSelectedTeam(event.target.value);
    setSelectedReport('');
    setShowReport(false);
  };

  const handleReportChange = event => {
    setSelectedReport(event.target.value);
  };

  const handleShowReportClick = () => {
    if (selectedReport) setShowReport(true);
  };

  const reportConfigs = {
    FSTOvertimeReport: fstOvertimeReport(translations),
    IncorrectCaseReport: incorrectCaseReport(translations),
    BenchRepairReport: benchRepairReport(translations),
    RltWorklogReport: rltWorklogReport,
    CommDeviceAudit: commAuditLog,
    CZGenesysCloudSurvey: czSurvey(translations),
    CLGenesysCloudSurvey: clccSurvey,
    CaliforniaPTLReport: californiaPTLReport,
    ForwardToSiteReport: forwardToSiteReport,
    GNIEUpdateRequestReport: gnieUpdateRequestReport,
    RetailerInfoChangeReport: retailerInfoChangeReport,
    RICreditLogReport: riCreditLogReport,
    WARetailerTrainingRequestReport: washingtonRetailerTrainingRequestReport,
    NYKeyRequestReport: nyKeyRequestReport,
    DispatchStatusChangeRequestReport: dispatchStatusChangeReport,
    ConsumableSupplyReport: consumableSupplyReport,
    NRCDowntimeReport: nrcDowntimeReport,
    AntillesCreditLogReport: antillesCreditLogReport,
    MichiganIncidentLogReport: miIncidentLogReport,
    NJSuppressedReport: njSuppressedReport,
    NYAMLogReport: nyAmLogReport,
    StolenAndMissingInstantTicketReport: stolenTicketLogReport,
    FieldServiceScoreCard: fieldServiceScoreCard,
    TexasScratchTicketReport: texasScratchTicketReport,
    ITSTexasScratchTicketReport: itsTexasScratchTicketReport,
  };

  const renderReport = () => {
    if (!showReport) return null;

    const reportConfig = reports.find(
      form => form.componentKey === selectedReport,
    );

    if (!reportConfig?.componentKey) {
      return (
        <Typography>
          {translations.selectReport || 'Select a Report to begin'}
        </Typography>
      );
    }

    const config = reportConfigs[reportConfig.componentKey];

    // special case for iframe
    if (reportConfig.componentKey === 'FieldServiceScoreCard') {
      return (
        <iframe
          src={config.iframeUrl}
          style={{ width: '100%', height: '80vh', border: 'none' }}
          title='Field Service Score Card'
        />
      );
    }

    // default = DataGrid
    return (
      <DataGridComponent
        apiEndpoint={config.apiEndpoint}
        columns={config.columns}
        translations={translations}
        transformResponse={config.transformResponse}
      />
    );
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: 'var(--reach-blue)',
          height: '35px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'left',
          marginBottom: '50px',
        }}
      >
        <Typography
          sx={{ color: '#FFFFFF', fontFamily: 'BrightstarSans-Bold', ml: 2 }}
        >
          {translations.reports || 'Reports'}
        </Typography>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          margin: '20px',
          '--reports-layout-width': desktopReportsWidth,
        }}
      >
        <Grid container spacing={2}>
          {isMobile && (
            <Grid size={12}>
              <Button
                variant='outlined'
                onClick={() => setIsMenuVisible(!isMenuVisible)}
                sx={{
                  mb: 2,
                  flex: 1,
                  color: '#ffffff',
                  backgroundColor: isMenuVisible
                    ? 'var(--reach-blue)'
                    : 'var(--vega-blue)',
                  fontFamily: 'BrightstarSans-Regular',
                }}
              >
                {isMenuVisible
                  ? translations.hideFilters || 'Hide Filters'
                  : translations.selectFilters || 'Select Filters'}
              </Button>
            </Grid>
          )}
          {(!isMobile || isMenuVisible) && (
            <Grid
              order={{ xs: 1, md: 2 }}
              size={{
                xs: 12,
                md: 4,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: 2,
                  marginBottom: '20px',
                  height: '100%',
                  width: {
                    xs: 'auto',
                    md: 'var(--reports-layout-width, 90vw)',
                  },
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <DropDown
                    items={reportMenuData(translations).map(data => ({
                      value: data.country,
                      label: data.country,
                    }))}
                    selectedValue={selectedCountry}
                    onChange={handleCountryChange}
                    placeholder={translations.selectCountry || 'Select Country'}
                    sx={{ backgroundColor: '#ffffff' }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <DropDown
                    items={teams.map(team => ({
                      value: team.name,
                      label: team.name,
                    }))}
                    selectedValue={selectedTeam}
                    onChange={handleTeamChange}
                    placeholder={translations.selectTeam || 'Select Team'}
                    sx={{ backgroundColor: '#ffffff' }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <DropDown
                    items={reports.map(form => ({
                      value: form.componentKey,
                      label: form.name,
                    }))}
                    selectedValue={selectedReport}
                    onChange={handleReportChange}
                    placeholder={translations.selectReport || 'Select Report'}
                    sx={{ backgroundColor: '#ffffff' }}
                  />
                </Box>
                <Box sx={{ justifyContent: 'center' }}>
                  <Button
                    variant='outlined'
                    onClick={handleShowReportClick}
                    sx={{
                      mt: 2,
                      backgroundColor: selectedReport ? 'var(--vega-blue)' : '',
                      color: '#ffffff',
                      fontFamily: 'BrightstarSans-Regular',
                    }}
                    disabled={!selectedReport}
                  >
                    {translations.loadReport || 'Load Report'}
                  </Button>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
        <Grid
          order={{ xs: 2, md: 1 }}
          sx={{ mt: { xs: '10%', md: 'auto' } }}
          size={{
            xs: 12,
            md: 8,
          }}
        >
          {renderReport()}
        </Grid>
      </Box>
    </>
  );
};

export default Reports;
